import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Alert, ScrollView, Image, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import * as VideoThumbnails from 'expo-video-thumbnails';
import * as Crypto from 'expo-crypto';
import { analyzeImage, analyzeVideoFrames } from '../lib/gemini';
import type { AnalyzedItem } from '../lib/gemini';
import { analyzeProductImage, analyzeProductVideo, toAnalyzedItem } from '../lib/backendApi';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import type { MainStackParamList } from '../types/navigation';
import { extractVideoFrames } from '../lib/videoProcessing';
import { useTheme } from '../styles/ThemeProvider';

type Props = {
  navigation: NativeStackNavigationProp<MainStackParamList, 'AddItem'>;
  route: RouteProp<MainStackParamList, 'AddItem'>;
};

type AnalysisStep = 'uploading' | 'analyzing' | 'identifying' | 'pricing' | 'finalizing';

export default function AddItemScreen({ navigation }: Props) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        title: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
        },
        subtitle: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
        },
        options: {
          flex: 1,
          padding: theme.spacing.xl,
          gap: theme.spacing.lg,
        },
        option: {
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          padding: theme.spacing.xl,
          alignItems: 'center',
        },
        iconContainer: {
          width: 64,
          height: 64,
          borderRadius: theme.radius.radiusMd,
          backgroundColor: theme.colors.accent,
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: theme.spacing.md,
        },
        optionTitle: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.xs,
        },
        note: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginTop: theme.spacing.lg,
          marginBottom: theme.spacing.sm,
          paddingHorizontal: theme.spacing.xl,
        },
        manualEntryLink: {
          marginTop: theme.spacing.xl,
          alignItems: 'center',
        },
        manualEntryText: {
          ...theme.typography.md,
          color: theme.colors.accent,
          textDecorationLine: 'underline',
        },
        loadingContainer: {
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing.xl,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        loadingSpinner: {
          marginBottom: theme.spacing.lg,
        },
        loadingTitle: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.xl,
        },
        loadingText: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginTop: theme.spacing.md,
        },
        checklistContainer: {
          width: '100%',
          maxWidth: 400,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          padding: theme.spacing.lg,
        },
        checklistItem: {
          flexDirection: 'row',
          alignItems: 'center',
          paddingVertical: theme.spacing.md,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        checklistIcon: {
          fontSize: 20,
          width: 32,
          fontFamily: theme.fontFamily.body,
          color: theme.colors.textTertiary,
        },
        checklistIconComplete: {
          color: theme.colors.accent,
        },
        checklistIconActive: {
          color: theme.colors.accent,
        },
        checklistText: {
          flex: 1,
          ...theme.typography.md,
          color: theme.colors.textTertiary,
        },
        checklistTextComplete: {
          color: theme.colors.textSecondary,
          textDecorationLine: 'line-through',
        },
        checklistTextActive: {
          color: theme.colors.textPrimary,
          fontFamily: theme.fontFamily.bodyMedium,
        },
        label: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textSecondary,
          marginBottom: theme.spacing.sm,
        },
        input: {
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          marginBottom: theme.spacing.lg,
          ...theme.typography.md,
          color: theme.colors.textPrimary,
        },
        readOnlyInput: {
          backgroundColor: theme.colors.backgroundElevated,
        },
        readOnlyText: {
          ...theme.typography.md,
          color: theme.colors.textPrimary,
        },
        optionSubtitle: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginTop: theme.spacing.xs,
        },
        photoPreviewContainer: {
          flex: 1,
          padding: theme.spacing.lg,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        thumbnailScroll: {
          flexGrow: 0,
        },
        thumbnailWrapper: {
          marginRight: theme.spacing.md,
          position: 'relative',
        },
        thumbnail: {
          width: 120,
          height: 120,
          borderRadius: theme.radius.radiusMd,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
        },
        removePhotoButton: {
          position: 'absolute',
          top: -8,
          right: -8,
          backgroundColor: theme.colors.backgroundElevated,
          borderRadius: 12,
        },
        emptyPhotoState: {
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
        },
        emptyPhotoText: {
          ...theme.typography.md,
          color: theme.colors.textTertiary,
          marginTop: theme.spacing.md,
        },
        multiPhotoActions: {
          padding: theme.spacing.lg,
          gap: theme.spacing.md,
          backgroundColor: theme.colors.backgroundPrimary,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
        },
        actionButton: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          padding: theme.spacing.lg,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          gap: theme.spacing.sm,
        },
        takeAnotherButton: {
          backgroundColor: 'transparent',
          borderColor: theme.colors.accent,
        },
        analyzeButton: {
          backgroundColor: theme.colors.accent,
          borderColor: theme.colors.accent,
        },
        cancelButton: {
          backgroundColor: 'transparent',
        },
        actionButtonText: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        analyzeButtonText: {
          color: theme.colors.backgroundPrimary,
        },
        cancelButtonText: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
        },
        videoIconContainer: {
          backgroundColor: theme.colors.accentDisabled,
        },
        videoPreviewContainer: {
          flex: 1,
          padding: theme.spacing.lg,
          backgroundColor: theme.colors.backgroundPrimary,
          justifyContent: 'center',
          alignItems: 'center',
        },
        videoPlaceholder: {
          alignItems: 'center',
          justifyContent: 'center',
          padding: theme.spacing.xxl,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        videoPlaceholderText: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
          marginTop: theme.spacing.lg,
        },
        videoInfoText: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.sm,
        },
      }),
    [theme],
  );
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [processingCount, setProcessingCount] = useState(0);
  const [capturedPhotos, setCapturedPhotos] = useState<string[]>([]);
  const [isCapturingMultiple, setIsCapturingMultiple] = useState(false);
  const [capturedVideo, setCapturedVideo] = useState<string | null>(null);
  const [capturedVideoDurationMs, setCapturedVideoDurationMs] = useState<number | null>(null);
  const [isCapturingVideo, setIsCapturingVideo] = useState(false);
  const [currentStep, setCurrentStep] = useState<AnalysisStep>('uploading');
  const [completedSteps, setCompletedSteps] = useState<Set<AnalysisStep>>(new Set());
  const [isVideo, setIsVideo] = useState(false);

  const markStepComplete = (step: AnalysisStep) => {
    console.log(`[PROGRESS] Marking step complete: ${step}`);
    setCompletedSteps(prev => new Set([...prev, step]));
  };

  const setAnalysisStep = (step: AnalysisStep) => {
    console.log(`[PROGRESS] Setting current step: ${step}`);
    setCurrentStep(step);
  };

  const resetAnalysisSteps = () => {
    setCompletedSteps(new Set());
    setCurrentStep('uploading');
    setIsVideo(false);
  };

  const handleCameraPress = async () => {
    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission Denied', 'Sorry, we need camera permissions to make this work!');
        return;
      }

      // Start multi-photo capture mode
      setIsCapturingMultiple(true);
      setCapturedPhotos([]);
      
      // Take the first photo
      await captureAnotherPhoto();
    } catch (error) {
      console.error('[ERROR] Camera Error:', error);
      Alert.alert('Camera Error', 'Failed to access camera or process image.');
      setIsCapturingMultiple(false);
    }
  };

  const captureAnotherPhoto = async () => {
    try {
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        aspect: [4, 3],
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const newPhotoUri = result.assets[0].uri;
        setCapturedPhotos(prev => [...prev, newPhotoUri]);
      }
    } catch (error) {
      console.error('[ERROR] Camera Error:', error);
      Alert.alert('Camera Error', 'Failed to capture photo.');
    }
  };

  const handleRemovePhoto = (index: number) => {
    setCapturedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleCancelMultiCapture = () => {
    setCapturedPhotos([]);
    setIsCapturingMultiple(false);
  };

  const handleAnalyzeMultiplePhotos = async () => {
    if (capturedPhotos.length === 0) {
      Alert.alert('No Photos', 'Please take at least one photo.');
      return;
    }

    // If only 1 photo, use the backend API flow
    if (capturedPhotos.length === 1) {
      await processAndUploadImage(capturedPhotos[0]);
      setCapturedPhotos([]);
      setIsCapturingMultiple(false);
      return;
    }

    // Multiple photos: use the old frontend flow
    await processAndUploadMultiplePhotosOfSameItem(capturedPhotos);
    setCapturedPhotos([]);
    setIsCapturingMultiple(false);
  };

  const handleVideoPress = async () => {
    // Check if running on web
    if (Platform.OS === 'web') {
      Alert.alert(
        'Feature Not Available on Web',
        'Video capture requires a native mobile app. Please use:\n\n• Expo Go app on iOS/Android\n• Native mobile build\n\nFor now, use the "Capture Photos" option which works on all platforms.',
        [{ text: 'OK' }]
      );
      return;
    }

    // Show guidance before recording
    Alert.alert(
      'Video Recording Tips',
      '📹 For best AI analysis:\n\n' +
      '• Use good lighting (natural light preferred)\n' +
      '• Plain background (avoid clutter)\n' +
      '• Rotate product slowly (360°)\n' +
      '• Keep product centered\n' +
      '• Hold steady for 3-5 seconds per angle\n' +
      '• Record for 10-15 seconds\n\n' +
      'Ready to record?',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Start Recording', onPress: () => startVideoRecording() }
      ]
    );
  };

  const startVideoRecording = async () => {
    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission Denied', 'Sorry, we need camera permissions to make this work!');
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Videos,
        videoMaxDuration: 15, // 15 second limit (meets 10-15s requirement)
        // Use highest quality available - iOS will use 1080p or 4K based on device
        videoQuality: ImagePicker.UIImagePickerControllerQualityType.High,
        // Allow editing to trim if needed
        allowsEditing: false,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const videoAsset = result.assets[0];
        const videoUri = videoAsset.uri;
        
        // Validate video before proceeding
        const isValid = await validateVideo(videoAsset);
        if (!isValid) {
          return; // Validation will show error alert
        }
        
        setCapturedVideo(videoUri);
        setCapturedVideoDurationMs(videoAsset.duration ?? null);
        setIsCapturingVideo(true);
      }
    } catch (error) {
      console.error('[ERROR] Video Capture Error:', error);
      Alert.alert('Video Error', 'Failed to capture video.');
    }
  };

  const validateVideo = async (videoAsset: ImagePicker.ImagePickerAsset): Promise<boolean> => {
    try {
      console.log('[DEBUG] Validating video:', {
        uri: videoAsset.uri,
        width: videoAsset.width,
        height: videoAsset.height,
        duration: videoAsset.duration,
        fileSize: videoAsset.fileSize,
      });

      // Check minimum resolution (1080p = 1920x1080)
      const minResolution = 1080;
      const videoResolution = Math.max(videoAsset.width || 0, videoAsset.height || 0);
      
      if (videoResolution < minResolution) {
        Alert.alert(
          'Low Video Quality',
          `Video resolution (${videoAsset.width}x${videoAsset.height}) is below recommended 1080p.\n\nFor best AI analysis, please:\n• Use a device with better camera\n• Check camera settings\n• Ensure good lighting\n\nContinue anyway?`,
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Continue', onPress: () => {} }
          ]
        );
        return true; // Allow to continue with warning
      }

      // Check duration (should be 10-15 seconds)
      const duration = videoAsset.duration || 0;
      if (duration < 5000) { // Less than 5 seconds
        Alert.alert(
          'Video Too Short',
          'Video should be at least 10 seconds for best analysis. Please record a longer video showing the product from multiple angles.',
          [{ text: 'OK' }]
        );
        return false;
      }

      // Check file size (warn if too large, might indicate issues)
      const maxFileSize = 100 * 1024 * 1024; // 100MB
      if (videoAsset.fileSize && videoAsset.fileSize > maxFileSize) {
        Alert.alert(
          'Large File Size',
          'Video file is quite large. This may take longer to process. Continue?',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Continue', onPress: () => {} }
          ]
        );
      }

      console.log('[DEBUG] Video validation passed');
      return true;
    } catch (error) {
      console.error('[ERROR] Video validation error:', error);
      return true; // Allow to continue if validation fails
    }
  };

  const handleCancelVideoCapture = () => {
    setCapturedVideo(null);
    setCapturedVideoDurationMs(null);
    setIsCapturingVideo(false);
  };

  const handleAnalyzeVideo = async () => {
    if (!capturedVideo) {
      Alert.alert('No Video', 'Please record a video first.');
      return;
    }

    await processAndAnalyzeVideo(capturedVideo, capturedVideoDurationMs);
  };

  const processAndAnalyzeVideo = async (videoUri: string, durationMs: number | null) => {
    if (!user) {
      Alert.alert('Not Logged In', 'You must be logged in to add items.');
      return;
    }

    resetAnalysisSteps();
    setIsVideo(true);
    setLoading(true);
    setAnalysisStep('uploading');
    setIsCapturingVideo(false);

    try {
      console.log('[DEBUG] Starting video processing:', videoUri);

      // Extract 10 frames spaced evenly across the actual video duration.
      // 10 frames matches the backend's MAX_PRODUCTS_PER_VIDEO cap so each
      // detected product is likely to appear in at least one frame.
      const frameBase64Images = await extractVideoFrames(
        videoUri,
        10,
        durationMs ?? undefined,
      );
      console.log(
        `[DEBUG] Extracted ${frameBase64Images.length} frames from video (durationMs=${durationMs})`,
      );

      if (frameBase64Images.length === 0) {
        Alert.alert('Error', 'Failed to extract video frames. Please try again.');
        setLoading(false);
        return;
      }

      markStepComplete('uploading');
      setAnalysisStep('analyzing');
      await new Promise(resolve => setTimeout(resolve, 100));

      // Send base64 frames directly to backend (FAST PATH - no upload!)
      console.log(`[DEBUG] Sending ${frameBase64Images.length} base64 video frames to backend`);
      const productId = Crypto.randomUUID();

      const backendPromise = analyzeProductVideo(
        frameBase64Images,
        productId,
        false,
        user?.id,
      );

      // Show optimistic progress while backend processes (run in parallel)
      const progressPromise = (async () => {
        await new Promise(resolve => setTimeout(resolve, 1200));
        markStepComplete('analyzing');
        setAnalysisStep('identifying');

        await new Promise(resolve => setTimeout(resolve, 1200));
        markStepComplete('identifying');
        setAnalysisStep('pricing');
      })();

      // Wait for backend to complete (progress will continue in parallel)
      const backendResponse = await backendPromise;

      // Ensure progress animation completes before proceeding
      await progressPromise;

      if (!backendResponse.success || backendResponse.products.length === 0) {
        Alert.alert('Analysis Failed', backendResponse.message || 'Could not identify items in the video.');
        setLoading(false);
        return;
      }

      console.log('[DEBUG] Backend video analysis complete:', {
        productCount: backendResponse.products.length,
        products: backendResponse.products.map(p => p.product_name),
      });

      // Map each detected product to an AnalyzedItem. Each product carries its
      // own server-generated product_id which we plumb through so downstream
      // screens can correlate with user_products rows the frontend will create.
      const analyzedItems = backendResponse.products.map(product => {
        const item = toAnalyzedItem(product);
        if (item._backendData) {
          item._backendData.product_id = product.product_id || productId;
        }
        return item;
      });
      markStepComplete('pricing');
      setAnalysisStep('finalizing');

      // Small delay to show the final step before navigation
      await new Promise(resolve => setTimeout(resolve, 300));
      markStepComplete('finalizing');

      // Backend returns storage_path array after uploading base64 frames.
      // All N products share the same frame list in v1 (no per-product crops).
      const storagePaths = backendResponse.storage_path as string[];
      console.log(
        `[DEBUG] Backend uploaded video frames to ${storagePaths?.length || 0} path(s);`,
        `${analyzedItems.length} product(s) detected`,
      );

      navigation.navigate('AddAnalyzedItem', {
        items: analyzedItems,
        imageStoragePath: storagePaths[0],  // Primary image (first frame)
      });
    } catch (error: any) {
      console.error('[ERROR] Video processing/analysis failed:', error);
      
      // Provide helpful error message for web users
      if (Platform.OS === 'web') {
        Alert.alert(
          'Video Processing Not Available',
          'Video frame extraction requires native mobile features. Please use the mobile app (Expo Go or native build) to use video capture.\n\nAlternatively, use "Capture Photos" which works on all platforms.',
          [{ text: 'OK' }]
        );
      } else {
        Alert.alert('Error', `An error occurred: ${error.message || 'Please try again.'}`);
      }
    } finally {
      setLoading(false);
      setCapturedVideo(null);
      setCapturedVideoDurationMs(null);
    }
  };

  const handleGalleryPress = async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission Denied', 'Sorry, we need gallery permissions to make this work!');
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsMultipleSelection: true,
        selectionLimit: 10, // Limit to 10 images to prevent overwhelming the user
        aspect: [4, 3],
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        await processAndUploadMultipleImages(result.assets);
      }
    } catch (error) {
      console.error('[ERROR] Gallery Error:', error);
      Alert.alert('Gallery Error', 'Failed to access gallery or process images.');
      setLoading(false);
    }
  };

  const processAndUploadImage = async (imageUri: string) => {
    if (!user) {
      Alert.alert('Not Logged In', 'You must be logged in to add items.');
      return;
    }
    resetAnalysisSteps();
    setIsVideo(false);
    setLoading(true);
    
    try {
      console.log('[DEBUG] Starting image processing for URI:', imageUri);
      setAnalysisStep('uploading');

      // Step 1: Resize and convert to base64 (NO UPLOAD - OPTIMIZATION!)
      const manipResult = await ImageManipulator.manipulateAsync(
        imageUri,
        [{ resize: { width: 512 } }],
        {
          compress: 0.9,
          format: ImageManipulator.SaveFormat.JPEG,
          base64: true,
        }
      );

      const base64Data = manipResult.base64!;
      console.log('[DEBUG] Image prepared as base64:', { length: base64Data.length });

      markStepComplete('uploading');
      setAnalysisStep('analyzing');
      await new Promise(resolve => setTimeout(resolve, 100));

      // Step 2: Send base64 directly to backend (FAST PATH - no Supabase upload!)
      console.log('[DEBUG] Sending base64 to backend for analysis');
      const productId = Crypto.randomUUID();
      
      const backendPromise = analyzeProductImage(
        base64Data,        // Send base64 instead of storage path
        productId,
        false,
        user?.id,
        false,
        true               // isBase64 = true (NEW!)
      );
      
      // Show optimistic progress while backend processes
      const progressPromise = (async () => {
        await new Promise(resolve => setTimeout(resolve, 1000));
        markStepComplete('analyzing');
        setAnalysisStep('identifying');
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        markStepComplete('identifying');
        setAnalysisStep('pricing');
      })();
      
      const backendResponse = await backendPromise;
      await progressPromise;

      if (!backendResponse.success) {
        Alert.alert('Analysis Failed', backendResponse.message || 'Could not identify items in the image.');
        setLoading(false);
        return;
      }

      console.log('[DEBUG] Backend analysis complete:', {
        product: backendResponse.product_name,
        priceSource: backendResponse.price_data.source,
        status: backendResponse.price_discovery_status,
      });

      const analyzedItem = toAnalyzedItem(backendResponse);
      if (analyzedItem._backendData) analyzedItem._backendData.product_id = productId;
      markStepComplete('pricing');
      setAnalysisStep('finalizing');

      // Small delay to show the final step before navigation
      await new Promise(resolve => setTimeout(resolve, 300));
      markStepComplete('finalizing');

      // Backend returns storage_path after uploading base64
      const storagePath = backendResponse.storage_path as string;
      console.log('[DEBUG] Backend uploaded image to:', storagePath);

      navigation.navigate('AddAnalyzedItem', {
        items: [analyzedItem],
        imageStoragePath: storagePath,
      });
    } catch (error: any) {
      console.error('[ERROR] Image processing/upload failed:', error);
      Alert.alert('Error', `An error occurred: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const processAndUploadMultiplePhotosOfSameItem = async (imageUris: string[]) => {
    if (!user) {
      Alert.alert('Not Logged In', 'You must be logged in to add items.');
      return;
    }
    
    setLoading(true);
    
    try {
      console.log(`[DEBUG] Processing ${imageUris.length} photos of the same item`);
      
      const base64Images: string[] = [];

      // Resize and convert each image to base64 (NO UPLOAD - OPTIMIZATION!)
      for (let i = 0; i < imageUris.length; i++) {
        const imageUri = imageUris[i];
        console.log(`[DEBUG] Preparing photo ${i + 1}/${imageUris.length} as base64`);

        const manipResult = await ImageManipulator.manipulateAsync(
          imageUri,
          [{ resize: { width: 512 } }],
          {
            compress: 0.9,
            format: ImageManipulator.SaveFormat.JPEG,
            base64: true,
          }
        );

        if (manipResult.base64) {
          base64Images.push(manipResult.base64);
          console.log(`[DEBUG] Photo ${i + 1} prepared (${manipResult.base64.length} chars)`);
        }
      }

      if (base64Images.length === 0) {
        Alert.alert('Error', 'Failed to process any photos. Please try again.');
        return;
      }

      // Send base64 array directly to backend (FAST PATH!)
      console.log(`[DEBUG] Sending ${base64Images.length} base64 images to backend`);
      const productId = Crypto.randomUUID();
      const backendResponse = await analyzeProductImage(
        base64Images,   // Array of base64 strings
        productId,
        false,
        user?.id,
        false,          // isVideo
        true            // isBase64 = true (NEW!)
      );

      if (!backendResponse.success) {
        Alert.alert('Analysis Failed', backendResponse.message || 'Could not identify items in the images.');
        setLoading(false);
        return;
      }

      console.log('[DEBUG] Backend analysis complete:', {
        product: backendResponse.product_name,
        priceSource: backendResponse.price_data.source,
        status: backendResponse.price_discovery_status,
      });

      const analyzedItem = toAnalyzedItem(backendResponse);
      if (analyzedItem._backendData) analyzedItem._backendData.product_id = productId;
      // Backend returns storage_path array after uploading base64 images
      const storagePaths = backendResponse.storage_path as string[];
      console.log('[DEBUG] Backend uploaded images to:', storagePaths);

      // Navigate to AddAnalyzedItem with all storage paths
      const itemImageMap: { [itemIndex: number]: string[] } = {};
      itemImageMap[0] = storagePaths;

      navigation.navigate('AddAnalyzedItem', {
        items: [analyzedItem],
        imageStoragePath: storagePaths[0], // Primary image
        itemImageMap: itemImageMap, // All images mapped to items
        multiplePhotosPerItem: true,
      });

    } catch (error: any) {
      console.error('[ERROR] Multi-photo processing failed:', error);
      Alert.alert('Error', `An error occurred: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const processAndUploadMultipleImages = async (imageAssets: ImagePicker.ImagePickerAsset[]) => {
    if (!user) {
      Alert.alert('Not Logged In', 'You must be logged in to add items.');
      return;
    }
    
    resetAnalysisSteps();
    setIsVideo(false);
    setLoading(true);
    setProcessingCount(imageAssets.length);
    setAnalysisStep('uploading');
    
    try {
      console.log(`[DEBUG] Starting bulk processing of ${imageAssets.length} images`);
      
      const analyzedItems: AnalyzedItem[] = [];
      const imageStoragePaths: string[] = [];

      // Start optimistic progress animation
      const progressPromise = (async () => {
        await new Promise(resolve => setTimeout(resolve, 800));
        markStepComplete('uploading');
        setAnalysisStep('analyzing');
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        markStepComplete('analyzing');
        setAnalysisStep('identifying');
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        markStepComplete('identifying');
        setAnalysisStep('pricing');
      })();

      // Process each image sequentially
      for (let i = 0; i < imageAssets.length; i++) {
        const asset = imageAssets[i];
        console.log(`[DEBUG] Processing image ${i + 1}/${imageAssets.length}: ${asset.uri}`);

        try {
          // Resize image
          const manipResult = await ImageManipulator.manipulateAsync(
            asset.uri,
            [{ resize: { width: 512 } }],
            {
              compress: 0.9,
              format: ImageManipulator.SaveFormat.JPEG,
              base64: true,
            }
          );

          // Convert base64 to ArrayBuffer for React Native upload
          const filePath = `${user.id}/${Crypto.randomUUID()}.jpg`;
          const base64Data = manipResult.base64!;
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let j = 0; j < binaryString.length; j++) {
            bytes[j] = binaryString.charCodeAt(j);
          }
          const arrayBuffer = bytes.buffer;

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('item-images')
            .upload(filePath, arrayBuffer, {
              contentType: 'image/jpeg',
              upsert: false,
            });

          if (uploadError) {
            console.error(`[ERROR] Failed to upload image ${i + 1}:`, uploadError);
            continue;
          }

          if (!uploadData?.path) {
            console.error(`[ERROR] Upload succeeded but no path returned for image ${i + 1}`);
            continue;
          }

          console.log(`[DEBUG] Image ${i + 1} uploaded successfully to:`, uploadData.path);

          // Call backend API for analysis
          const productId = Crypto.randomUUID();
          const backendResponse = await analyzeProductImage(
            uploadData.path,  // Single image path
            productId,
            false,  // forcePriceDiscovery
            user.id,  // userId
            false  // isVideo
          );

          if (!backendResponse.success) {
            console.warn(`[WARN] Backend analysis failed for image ${i + 1}, skipping`);
            continue;
          }

          console.log(`[DEBUG] Image ${i + 1} analysis complete:`, backendResponse.product_name);

          const analyzedItem = toAnalyzedItem(backendResponse);
          if (analyzedItem._backendData) analyzedItem._backendData.product_id = productId;
          analyzedItems.push(analyzedItem);
          imageStoragePaths.push(uploadData.path);

        } catch (imageError: any) {
          console.error(`[ERROR] Failed to process image ${i + 1}:`, imageError);
        }
      }

      if (analyzedItems.length === 0) {
        Alert.alert('No Items Found', 'Could not identify items in any of the selected images. Please try different images.');
        return;
      }

      // Wait for progress animation to complete
      await progressPromise;

      markStepComplete('pricing');
      setAnalysisStep('finalizing');

      // --- Group by instrument_id (exact match) ---
      // Items sharing the same backend instrument_id are the same product
      // photographed from different angles. Auto-merge them.
      const instrumentGroups = new Map<number, { items: AnalyzedItem[]; paths: string[] }>();
      const ungroupedItems: AnalyzedItem[] = [];
      const ungroupedPaths: string[] = [];

      analyzedItems.forEach((item, idx) => {
        const instrumentId = item._backendData?.instrument_id;
        if (instrumentId != null) {
          const existing = instrumentGroups.get(instrumentId);
          if (existing) {
            existing.items.push(item);
            existing.paths.push(imageStoragePaths[idx]);
          } else {
            instrumentGroups.set(instrumentId, {
              items: [item],
              paths: [imageStoragePaths[idx]],
            });
          }
        } else {
          // No instrument_id (backend error) — treat as unique
          ungroupedItems.push(item);
          ungroupedPaths.push(imageStoragePaths[idx]);
        }
      });

      // Auto-merge groups: pick highest-confidence analysis, combine image paths
      const mergedItems: AnalyzedItem[] = [];
      const mergedImageMap: { [itemIndex: number]: string | string[] } = {};

      for (const [, group] of instrumentGroups) {
        const bestItem = group.items.reduce((best, current) =>
          (current.confidenceScore || 0) > (best.confidenceScore || 0) ? current : best
        );
        const itemIndex = mergedItems.length;
        mergedItems.push(bestItem);
        mergedImageMap[itemIndex] = group.paths.length === 1 ? group.paths[0] : group.paths;
      }

      // Check if there are ungrouped items that need fuzzy duplicate detection
      if (ungroupedItems.length > 0) {
        // Add ungrouped items into the merged list without additional fuzzy matching
        ungroupedItems.forEach((item, idx) => {
          const itemIndex = mergedItems.length;
          mergedItems.push(item);
          mergedImageMap[itemIndex] = ungroupedPaths[idx];
        });
      }

      const totalMerged = analyzedItems.length - mergedItems.length;
      console.log(`[DEBUG] Grouped ${analyzedItems.length} images into ${mergedItems.length} items (${totalMerged} merged by instrument_id)`);

      // Small delay to show the final step before navigation
      await new Promise(resolve => setTimeout(resolve, 300));
      markStepComplete('finalizing');

      if (mergedItems.length > 1) {
        // Multiple distinct items remain after instrument_id grouping.
        // Route through GroupItemsScreen for fuzzy duplicate detection —
        // Gemini may have given different brands/models for different angles
        // of the same product (e.g. "Adidas" from front vs "Liverpool FC" from back).
        const finalPaths = Object.values(mergedImageMap).map(p =>
          Array.isArray(p) ? p[0] : p
        );
        navigation.navigate('GroupItems', {
          analyzedItems: mergedItems,
          imageStoragePaths: finalPaths,
        });
      } else {
        // Single item (or all merged into one) — go straight to save screen
        navigation.navigate('AddAnalyzedItem', {
          items: mergedItems,
          imageStoragePath: imageStoragePaths[0],
          itemImageMap: mergedImageMap,
          multiplePhotosPerItem: true,
        });
      }

    } catch (error: any) {
      console.error('[ERROR] Bulk image processing failed:', error);
      Alert.alert('Error', `An error occurred while processing images: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
      setProcessingCount(0);
    }
  };

  const handleManualEntry = () => {
    // Navigate to AddAnalyzedItem with an empty item and null image path
    navigation.navigate('AddAnalyzedItem', {
      items: [{
        name: '',
        description: '',
        category: '',
        condition: 'Good', // Changed from ''
        priceRange: null,
        selected: true,
        image_url: '',
        confidenceScore: 1,
        conditionScore: undefined,
        demandScore: undefined,
        missingInfo: undefined,
        lendingSuitability: undefined,
        purchaseDate: undefined,
        originalValue: undefined,
        depreciationRate: undefined,
        boundingBox: undefined,
        brand: undefined,
        model: undefined,
      }],
      imageStoragePath: null, // Use imageStoragePath
    });
  };

  const getStepLabel = (step: AnalysisStep): string => {
    const labels = {
      uploading: isVideo ? 'Processing video frames' : processingCount > 1 ? 'Uploading images' : 'Uploading image',
      analyzing: isVideo ? 'Analyzing product from multiple angles' : 'Analyzing product details',
      identifying: 'Identifying brand and model',
      pricing: 'Checking market prices',
      finalizing: 'Finalizing results',
    };
    return labels[step];
  };

  const getStepIcon = (step: AnalysisStep): string => {
    if (completedSteps.has(step)) return '✓';
    if (currentStep === step) return '⏳';
    return '○';
  };

  if (loading) {
    const steps: AnalysisStep[] = ['uploading', 'analyzing', 'identifying', 'pricing', 'finalizing'];
    
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color={theme.colors.textPrimary} style={styles.loadingSpinner} />
        <Text style={styles.loadingTitle}>Processing...</Text>
        <View style={styles.checklistContainer}>
          {steps.map((step) => (
            <View key={step} style={styles.checklistItem}>
              <Text style={[
                styles.checklistIcon,
                completedSteps.has(step) && styles.checklistIconComplete,
                currentStep === step && styles.checklistIconActive,
              ]}>
                {getStepIcon(step)}
              </Text>
              <Text style={[
                styles.checklistText,
                completedSteps.has(step) && styles.checklistTextComplete,
                currentStep === step && styles.checklistTextActive,
              ]}>
                {getStepLabel(step)}
              </Text>
            </View>
          ))}
        </View>
      </View>
    );
  }

  // Video capture mode UI
  if (isCapturingVideo && capturedVideo) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Video Captured</Text>
          <Text style={styles.subtitle}>15 second video ready for analysis</Text>
        </View>

        <View style={styles.videoPreviewContainer}>
          <View style={styles.videoPlaceholder}>
            <Ionicons name="videocam" size={64} color={theme.colors.textPrimary} />
            <Text style={styles.videoPlaceholderText}>Video Ready</Text>
            <Text style={styles.videoInfoText}>Duration: ~{Math.floor(Math.random() * 10 + 5)}s</Text>
          </View>
        </View>

        <View style={styles.multiPhotoActions}>
          <TouchableOpacity
            style={[styles.actionButton, styles.analyzeButton]}
            onPress={handleAnalyzeVideo}
          >
            <Ionicons name="checkmark-circle" size={24} color={theme.colors.backgroundPrimary} />
            <Text style={[styles.actionButtonText, styles.analyzeButtonText]}>
              Analyze Video
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionButton, styles.cancelButton]}
            onPress={handleCancelVideoCapture}
          >
            <Text style={styles.cancelButtonText}>Retake Video</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Multi-photo capture mode UI
  if (isCapturingMultiple) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Capture a Product</Text>
          <Text style={styles.subtitle}>{capturedPhotos.length} photo{capturedPhotos.length !== 1 ? 's' : ''} captured</Text>
        </View>

        <View style={styles.photoPreviewContainer}>
          {capturedPhotos.length > 0 ? (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.thumbnailScroll}>
              {capturedPhotos.map((uri, index) => (
                <View key={index} style={styles.thumbnailWrapper}>
                  <Image source={{ uri }} style={styles.thumbnail} />
                  <TouchableOpacity 
                    style={styles.removePhotoButton}
                    onPress={() => handleRemovePhoto(index)}
                  >
                    <Ionicons name="close-circle" size={24} color={theme.colors.destructive} />
                  </TouchableOpacity>
                </View>
              ))}
            </ScrollView>
          ) : (
            <View style={styles.emptyPhotoState}>
              <Ionicons name="camera-outline" size={64} color={theme.colors.textTertiary} />
              <Text style={styles.emptyPhotoText}>No photos yet</Text>
            </View>
          )}
        </View>

        <View style={styles.multiPhotoActions}>
          <TouchableOpacity
            style={[styles.actionButton, styles.takeAnotherButton]}
            onPress={captureAnotherPhoto}
          >
            <Ionicons name="camera" size={24} color={theme.colors.textPrimary} />
            <Text style={styles.actionButtonText}>Take Another Photo</Text>
          </TouchableOpacity>

          {capturedPhotos.length > 0 && (
            <TouchableOpacity
              style={[styles.actionButton, styles.analyzeButton]}
              onPress={handleAnalyzeMultiplePhotos}
            >
              <Ionicons name="checkmark-circle" size={24} color={theme.colors.backgroundPrimary} />
              <Text style={[styles.actionButtonText, styles.analyzeButtonText]}>
                Analyze {capturedPhotos.length} Photo{capturedPhotos.length !== 1 ? 's' : ''}
              </Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={[styles.actionButton, styles.cancelButton]}
            onPress={handleCancelMultiCapture}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <View style={styles.header}>
        <Text style={styles.title}>Add Items</Text>
      </View>

      <View style={styles.options}>
          <TouchableOpacity
          style={styles.option}
            onPress={handleCameraPress}
        >
          <View style={styles.iconContainer}>
            <Ionicons name="camera" size={40} color={theme.colors.textPrimary} />
          </View>
          <Text style={styles.optionTitle}>Capture Photos</Text>
          <Text style={styles.optionSubtitle}>Take one or more pics of a single product</Text>
          </TouchableOpacity>

          {Platform.OS !== 'web' && (
            <TouchableOpacity
              style={styles.option}
              onPress={handleVideoPress}
            >
              <View style={[styles.iconContainer, styles.videoIconContainer]}>
                <Ionicons name="videocam" size={40} color={theme.colors.textPrimary} />
              </View>
              <Text style={styles.optionTitle}>Record Video (HD)</Text>
              <Text style={styles.optionSubtitle}>10-15s video • 1080p+ • Best for detailed analysis</Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={styles.option}
            onPress={handleGalleryPress}
          >
            <View style={styles.iconContainer}>
              <Ionicons name="images-outline" size={40} color={theme.colors.textPrimary} />
            </View>
            <Text style={styles.optionTitle}>Choose from Gallery</Text>
            <Text style={styles.optionSubtitle}>Upload multiple images for one or more products</Text>
          </TouchableOpacity>

        <Text style={styles.note}>
          💡 Tip: HD video (1080p+) provides 360° views for superior AI analysis. Photos work for multiple angles. Gallery uploads analyze each image separately.
        </Text>

          <TouchableOpacity
          style={styles.manualEntryLink}
            onPress={handleManualEntry}
          >
          <Text style={styles.manualEntryText}>Add an item manually</Text>
          </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

